<script setup>
import { WdsBadge, WdsIcon } from '@wds/components';

const props = defineProps({
  eventName: {
    type: String,
    required: true,
  },
  eventLocation: {
    type: String,
    required: false,
    default: null,
  },
  eventDates: {
    type: Object,
    required: false,
    default: null,
    validator: (val) => val === null || (val?.startDate && val?.endDate),
  },
  eventAddress: {
    type: String,
    required: false,
    default: null,
  },
  eventPayment: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  eventTags: {
    type: Array,
    required: false,
    default: () => [],
    validator: (val) => Array.isArray(val) && val.every((tag) => tag?.icon && tag?.transcode),
  },
  eventAbout: {
    type: String,
    required: false,
    default: null,
  },
});

// ### COMPOSABLES
const {
  t,
  locale,
} = useI18n();
const { formatPeriodToHumanReadable } = useDateTime();

// ### COMPUTED
const hasValidDates = computed(() => {
  return props.eventDates?.startDate && props.eventDates?.endDate;
});

const hasValidTags = computed(() => {
  return Array.isArray(props.eventTags) && props.eventTags.length > 0;
});

const hasValidPaymentInfo = computed(() => {
  return props.eventPayment?.status;
});

const eventDatesFormatted = computed(() => {
  if (!hasValidDates.value) {
    return null;
  }

  return formatPeriodToHumanReadable(props.eventDates?.startDate, props.eventDates?.endDate, locale.value);
});
</script>

<template>
  <div class="event-heading-container">
    <p v-if="eventLocation">
      {{ eventLocation }}
    </p>
    <h1 class="event-name">
      {{ eventName }}
    </h1>
    <div class="event-details-container">
      <div
        v-if="hasValidDates"
        class="event-detail"
      >
        <WdsIcon asset="calendar" />
        <p>{{ eventDatesFormatted }}</p>
      </div>
      <div
        v-if="eventAddress"
        class="event-detail"
      >
        <WdsIcon asset="map-pin" />
        <p>{{ eventAddress }}</p>
      </div>
      <div
        v-if="hasValidPaymentInfo"
        class="event-detail"
      >
        <WdsIcon asset="money" />
        <p>
          {{ eventPayment.status === 'free' ? t('t_OGEVENTPAYMENTSTATUSFREE') : t('t_OGEVENTPAYMENTSTATUSPAID') }}
        </p>
        <p v-if="eventPayment.ticketsLink">
          ({{ t('t_OGEVENTTICKETED') }})
        </p>
      </div>
    </div>
    <div
      v-if="hasValidTags"
      class="event-tags"
    >
      <WdsBadge
        v-for="tag in eventTags"
        :key="tag.id"
        class="event-tag"
        theme="ink-lightest"
        :label="t(tag.transcode)"
        :aria-description="t(tag.transcode)"
        :icon="tag.icon"
      />
    </div>
    <div
      v-if="eventAbout"
      class="event-about"
    >
      <p>{{ eventAbout }}</p>
    </div>
    <a
      v-if="eventPayment.ticketsLink"
      :href="eventPayment.ticketsLink"
      class="event-more-details-from-organiser"
      target="_blank"
      rel="nofollow noopener noreferrer"
    >{{ t('t_OGEVENTMOREDETAILSFROMORGANISER') }}</a>
  </div>
</template>

<style lang="scss" scoped>
.event-heading-container {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-s;
  margin-top: $wds-spacing-m;

  @include body-1-reg;

  .event-name {
    @include title-2-bld;

    overflow-wrap: break-word;
  }

  .event-details-container {
    display: flex;
    flex-direction: column;
    gap: $wds-spacing-s;

    .event-detail {
      display: inline-flex;
      gap: $wds-spacing-xs;
      align-items: center;

      @include body-3-reg;
    }
  }

  .event-tags {
    display: flex;
    gap: $wds-spacing-s;
    flex-wrap: wrap;

    .event-tag {
      fill: $wds-color-ink;
      color: $wds-color-ink;
    }
  }

  .event-about {
    margin-top: $wds-spacing-m;

    > p {
      white-space: pre-line;
    }
  }

  .event-more-details-from-organiser {
    display: inline;
    text-decoration: underline;
    align-self: flex-start;

    &:focus-visible {
      outline: 2px solid $wds-color-ink-darker !important;
      outline-offset: 2px !important;
      border-radius: 4px !important;
    }
  }

  @include tablet-large {
    .event-name {
      @include title-1-bld;
    }

    .event-details-container {
      flex-flow: row wrap;
      gap: $wds-spacing-m;
    }
  }

  @include desktop-large {
    margin-top: 0;
  }
}
</style>
